import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AcademyPrograms from "../components/app/AcademyPrograms";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageToggle from "../components/app/LanguageToggle";
import AuthImage from "../components/app/AuthImage";
import Logo from "../components/app/Logo";
import ContactActions from "../components/app/ContactActions";
import { toast } from "sonner";
import { Star, MapPin, Sparkles, ArrowLeft, Building2, Users, Trophy, Award, Phone, MessageCircle, MessageSquare, Instagram, Send, X, Check } from "lucide-react";

export default function AcademyPublicProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    api.get(`/public/academies/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="card-sharp p-10 text-center">
        <Building2 className="w-12 h-12 text-white/30 mx-auto mb-3" />
        <div className="font-oswald text-2xl tracking-tight mb-2">{lang === "bg" ? "Не е намерена" : "Not found"}</div>
        <div className="text-white/40 text-xs font-mono mb-5">{err}</div>
        <Link to="/find-coaches" className="btn-primary px-5 py-2.5 text-xs inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> {lang === "bg" ? "Назад към открий" : "Back to discover"}
        </Link>
      </div>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="pitch-loader"><div className="ball" /></div></div>;
  const { academy, coaches } = data;

  return (
    <div className="min-h-screen pb-16">
      <header className="header-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-4">
          <Link to={user ? "/" : "/"} className="flex items-center"><Logo size={26} wordmark /></Link>
          <div className="flex items-center gap-3">
            <button onClick={() => nav(-1)} className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1" data-testid="academy-back">
              <ArrowLeft className="w-3 h-3" /> {lang === "bg" ? "Назад" : "Back"}
            </button>
            <LanguageToggle />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="academy-public-profile">
        <AcademyPrograms academyId={id} />
        {/* Hero */}
        <div className="relative card-sharp overflow-hidden grain animate-fade-up" style={{ opacity: 0 }}>
          {academy.academy_banner && (
            <div className="absolute inset-0 z-0">
              <AuthImage src={academy.academy_banner} className="w-full h-full object-cover opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/85 to-[#0A1628]/40" />
            </div>
          )}
          <div className="spotlight" />
          {!academy.academy_banner && <div className="absolute inset-0 pitch-field opacity-40" />}
          <div className="relative z-10 p-6 md:p-10 flex flex-col md:flex-row gap-6 items-start">
            <div className="w-24 h-24 rounded-2xl bg-pitch/10 border-2 border-pitch/40 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {academy.academy_logo ? (
                <AuthImage src={academy.academy_logo} className="w-full h-full object-cover" fallback={<Building2 className="w-12 h-12 text-pitch" />} />
              ) : (
                <Building2 className="w-12 h-12 text-pitch" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="label-mono text-pitch">// ACADEMY</span>
                {academy.is_pro_academy && <span className="chip-badge gold"><Sparkles className="w-3 h-3" /> PRO</span>}
                {(academy.badges || []).map((b) => <span key={b} className="chip-badge gold">{b.replace("_", " ").toUpperCase()}</span>)}
                {academy.academy_size && <span className="chip-badge">{academy.academy_size}</span>}
              </div>
              <h1 className="font-oswald uppercase text-3xl sm:text-5xl tracking-tight leading-none">
                {academy.academy_name || academy.name}
              </h1>
              {academy.city && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-white/55 font-mono text-xs">
                  <MapPin className="w-3.5 h-3.5 text-pitch" /> {academy.city}
                </div>
              )}
              {academy.academy_description && (
                <p className="text-white/65 mt-4 max-w-2xl">{academy.academy_description}</p>
              )}
              <div className="mt-6 flex gap-8">
                <div>
                  <div className="label-mono">{lang === "bg" ? "Треньори" : "Coaches"}</div>
                  <div className="font-oswald text-4xl text-pitch text-glow-emerald leading-none mt-1">{coaches.length}</div>
                </div>
                {academy.players_count != null && (
                  <div>
                    <div className="label-mono">{lang === "bg" ? "Играчи" : "Players"}</div>
                    <div className="font-oswald text-4xl text-white leading-none mt-1">{academy.players_count}</div>
                  </div>
                )}
              </div>
              {(academy.phone || academy.has_phone || academy.instagram || academy.whatsapp || academy.viber) && (
                <div className="mt-5">
                  <ContactActions phone={academy.phone} hasPhone={!!academy.has_phone} instagram={academy.instagram} revealed={!!academy.contact_revealed} lang={lang} />
                </div>
              )}
              {/* Lead-Gen primary CTA — Academies operate as organizations, not bookable
                  coaches; visitors send an application instead of booking a session. */}
              <div className="mt-5 flex flex-wrap gap-3" data-testid="academy-leadgen-ctas">
                <button
                  onClick={() => setShowApply(true)}
                  className="btn-primary px-6 py-3 text-xs inline-flex items-center gap-2"
                  data-testid="academy-apply-btn"
                >
                  <Send className="w-4 h-4" />
                  {lang === "bg" ? "Кандидатствай / Запиши се" : "Apply / Join"}
                </button>
                <a
                  href="#contact-academy"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("contact-academy")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2"
                  data-testid="academy-contact-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  {lang === "bg" ? "Свържи се" : "Get in touch"}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Academy — Iter 24 */}
        {(academy.phone || academy.has_phone || academy.whatsapp || academy.viber || academy.instagram) && (
          <div className="card-sharp p-6 sm:p-8 rounded-lg" id="contact-academy" data-testid="contact-academy-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-pitch/10 border border-pitch/40 flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-pitch" />
              </div>
              <div>
                <div className="label-mono">// {lang === "bg" ? "Свържи се" : "Get in touch"}</div>
                <h2 className="font-oswald uppercase text-xl sm:text-2xl tracking-tight">
                  {lang === "bg" ? "Контакт с академията" : "Contact academy"}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {academy.phone ? (
                <a
                  href={`tel:${academy.phone}`}
                  className="border border-white/10 hover:border-pitch/55 hover:bg-pitch/[0.03] rounded-md p-4 flex items-center gap-3 transition-all group"
                  data-testid="academy-call-btn"
                >
                  <span className="w-9 h-9 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0 group-hover:bg-pitch group-hover:text-black transition-colors">
                    <Phone className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">{lang === "bg" ? "Обади се" : "Call"}</div>
                    <div className="font-oswald tracking-tight text-sm truncate">{academy.phone}</div>
                  </div>
                </a>
              ) : null}

              {academy.whatsapp && (
                <a
                  href={`https://wa.me/${academy.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="border border-white/10 hover:border-blue-400/55 hover:bg-blue-400/[0.05] rounded-md p-4 flex items-center gap-3 transition-all group"
                  data-testid="academy-whatsapp-btn"
                >
                  <span className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500 group-hover:text-black transition-colors">
                    <MessageCircle className="w-4 h-4 text-blue-400 group-hover:text-black" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">WhatsApp</div>
                    <div className="font-oswald tracking-tight text-sm truncate">{academy.whatsapp}</div>
                  </div>
                </a>
              )}

              {academy.viber && (
                <a
                  href={`viber://chat?number=${encodeURIComponent(academy.viber)}`}
                  className="border border-white/10 hover:border-purple-400/55 hover:bg-purple-400/[0.05] rounded-md p-4 flex items-center gap-3 transition-all group"
                  data-testid="academy-viber-btn"
                >
                  <span className="w-9 h-9 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500 group-hover:text-white transition-colors">
                    <MessageSquare className="w-4 h-4 text-purple-400 group-hover:text-white" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">Viber</div>
                    <div className="font-oswald tracking-tight text-sm truncate">{academy.viber}</div>
                  </div>
                </a>
              )}

              {academy.instagram && (
                <a
                  href={academy.instagram.startsWith("http") ? academy.instagram : `https://instagram.com/${academy.instagram.replace(/^@/, "")}`}
                  target="_blank" rel="noopener noreferrer"
                  className="border border-white/10 hover:border-pink-400/55 hover:bg-pink-400/[0.05] rounded-md p-4 flex items-center gap-3 transition-all group"
                  data-testid="academy-instagram-btn"
                >
                  <span className="w-9 h-9 rounded-full bg-pink-500/10 border border-pink-500/30 flex items-center justify-center flex-shrink-0 group-hover:bg-pink-500 group-hover:text-white transition-colors">
                    <Instagram className="w-4 h-4 text-pink-400 group-hover:text-white" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">Instagram</div>
                    <div className="font-oswald tracking-tight text-sm truncate">{academy.instagram}</div>
                  </div>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Coaches roster */}
        <div>
          <div className="label-mono mb-3">// {lang === "bg" ? "Треньорски състав" : "Coaching roster"}</div>
          {coaches.length === 0 ? (
            <div className="card-sharp p-10 text-center text-white/35 font-mono text-xs">
              {lang === "bg" ? "Все още няма треньори." : "No coaches listed yet."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {coaches.map((c, i) => (
                <Link key={c.id} to={`/coach/${c.id}`} className="card-coach p-5 block group" data-testid={`roster-coach-${c.id}`} style={{ animationDelay: `${i * 35}ms`, opacity: 0 }}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-pitch/10 border border-pitch/40 flex items-center justify-center flex-shrink-0">
                      {c.photo_url ? (
                        <AuthImage src={c.photo_url} className="w-full h-full object-cover rounded-full" fallback={<Trophy className="w-5 h-5 text-pitch" />} />
                      ) : (
                        <Trophy className="w-5 h-5 text-pitch" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-oswald uppercase text-base tracking-tight truncate">{c.name}</div>
                      <div className="flex items-center gap-1 text-xs mt-1">
                        <Star className="w-3 h-3 text-pitch fill-pitch" />
                        <span className="font-mono text-white/65">{(c.rating || 0).toFixed(1)}</span>
                        {c.specialty && <span className="text-white/30 mx-1">·</span>}
                        {c.specialty && <span className="text-[10px] font-mono uppercase tracking-widest text-white/45">{c.specialty}</span>}
                      </div>
                    </div>
                    {c.hourly_rate != null && (
                      <div className="text-right">
                        <div className="font-oswald text-pitch text-lg leading-none">€{c.hourly_rate}</div>
                        <div className="text-[10px] font-mono text-white/40 mt-0.5">/{lang === "bg" ? "ч" : "h"}</div>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Optional achievements */}
        {(academy.achievements || []).length > 0 && (
          <div className="card-sharp p-5">
            <div className="label-mono mb-3 flex items-center gap-1.5"><Award className="w-3 h-3 text-pitch" /> // {lang === "bg" ? "Постижения" : "Achievements"}</div>
            <ul className="space-y-2">
              {(academy.achievements || []).map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Sparkles className="w-3.5 h-3.5 text-pitch mt-0.5 flex-shrink-0" />
                  <span className="text-white/80">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showApply && (
        <ApplyModal
          academyId={id}
          academyName={academy.academy_name || academy.name}
          lang={lang}
          user={user}
          onClose={() => setShowApply(false)}
        />
      )}
    </div>
  );
}

/* ---------------- Apply / Join modal ---------------- */
function ApplyModal({ academyId, academyName, lang, user, onClose }) {
  const [form, setForm] = useState({
    parent_name: user?.name || "",
    parent_email: user?.email || "",
    parent_phone: "",
    player_name: "",
    player_age: "",
    player_level: "beginner",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim()) {
      setErr(lang === "bg" ? "Името е задължително" : "Name is required");
      return;
    }
    if (!form.parent_phone.trim() && !form.parent_email.trim()) {
      setErr(lang === "bg" ? "Имейл или телефон е задължителен" : "Email or phone is required");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      if (payload.player_age === "" || payload.player_age == null) {
        delete payload.player_age;
      } else {
        payload.player_age = Number(payload.player_age);
      }
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      await api.post(`/public/academies/${academyId}/applications`, payload);
      setDone(true);
      toast.success(lang === "bg" ? "Заявката е изпратена!" : "Application sent!");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || (lang === "bg" ? "Грешка" : "Error"));
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6 animate-fade-up"
      onClick={onClose}
      data-testid="apply-modal"
    >
      <div className="bg-[#0A1628] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628] z-10">
          <div>
            <div className="label-mono text-pitch">// {lang === "bg" ? "Кандидатствай" : "Apply"}</div>
            <h2 className="font-oswald uppercase text-xl tracking-tight truncate">{academyName}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/5 rounded-md transition-colors" data-testid="close-apply-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        {done ? (
          <div className="p-10 text-center" data-testid="apply-success">
            <div className="w-14 h-14 rounded-full bg-pitch/15 border border-pitch/40 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-pitch" />
            </div>
            <div className="font-oswald uppercase text-2xl tracking-tight mb-2">
              {lang === "bg" ? "Заявката е изпратена!" : "Application sent!"}
            </div>
            <p className="text-sm text-white/55 mb-5">
              {lang === "bg"
                ? "Академията ще се свърже с теб скоро."
                : "The academy will reach out to you shortly."}
            </p>
            <button onClick={onClose} className="btn-primary px-5 py-2.5 text-xs">
              {lang === "bg" ? "Затвори" : "Close"}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3" data-testid="apply-form">
            <ApplyField
              label={lang === "bg" ? "Твоето име *" : "Your name *"}
              value={form.parent_name}
              onChange={(v) => setForm({ ...form, parent_name: v })}
              testId="apply-parent-name"
              required
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ApplyField
                label={lang === "bg" ? "Имейл" : "Email"}
                type="email"
                value={form.parent_email}
                onChange={(v) => setForm({ ...form, parent_email: v })}
                testId="apply-parent-email"
              />
              <ApplyField
                label={lang === "bg" ? "Телефон" : "Phone"}
                value={form.parent_phone}
                onChange={(v) => setForm({ ...form, parent_phone: v })}
                testId="apply-parent-phone"
              />
            </div>
            <div className="border-t border-white/8 pt-3 mt-3">
              <div className="label-mono mb-2">// {lang === "bg" ? "Играч" : "Player"}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ApplyField
                  label={lang === "bg" ? "Име на детето" : "Player name"}
                  value={form.player_name}
                  onChange={(v) => setForm({ ...form, player_name: v })}
                  testId="apply-player-name"
                />
                <ApplyField
                  label={lang === "bg" ? "Възраст" : "Age"}
                  type="number"
                  value={form.player_age}
                  onChange={(v) => setForm({ ...form, player_age: v })}
                  testId="apply-player-age"
                />
              </div>
              <div className="mt-3">
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Ниво" : "Level"}</label>
                <select
                  value={form.player_level}
                  onChange={(e) => setForm({ ...form, player_level: e.target.value })}
                  className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded"
                  data-testid="apply-player-level"
                >
                  <option value="beginner">{lang === "bg" ? "Начинаещ" : "Beginner"}</option>
                  <option value="intermediate">{lang === "bg" ? "Среден" : "Intermediate"}</option>
                  <option value="advanced">{lang === "bg" ? "Напреднал" : "Advanced"}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1.5">{lang === "bg" ? "Съобщение" : "Message"}</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                placeholder={lang === "bg" ? "Цели, предпочитани часове, въпроси..." : "Goals, preferred hours, questions..."}
                className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded resize-none"
                data-testid="apply-message"
                maxLength={2000}
              />
            </div>
            {err && (
              <div className="text-red-400 text-xs font-mono" data-testid="apply-error">
                {err}
              </div>
            )}
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={onClose} className="flex-1 btn-secondary py-3 text-xs">
                {lang === "bg" ? "Отказ" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex-1 btn-primary py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="apply-submit"
              >
                {busy ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Send className="w-4 h-4" />}
                {lang === "bg" ? "Изпрати" : "Send"}
              </button>
            </div>
            <div className="text-[10px] text-white/30 font-mono">
              {lang === "bg"
                ? "Заявката се изпраща директно на академията. Те ще се свържат с теб."
                : "The application goes directly to the academy. They will contact you."}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ApplyField({ label, value, onChange, type = "text", testId, required }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded text-sm"
        data-testid={testId}
      />
    </div>
  );
}
